import {
  VERIFIED_EXACT_OVERRIDES,
  VERIFIED_RULE_OVERRIDES,
  VERIFIED_FIX_REPLACEMENTS,
} from '../config/verifiedHindiGlossary';

// Single source of truth for the language preference lives in
// bilingualResolver (Firestore-backed). Re-export so legacy imports keep working.
export {
  LANGUAGE_OPTIONS,
  getPreferredLanguage,
  setPreferredLanguage,
} from './bilingualResolver';

const translationCache = new Map();

// Request queue to prevent rate limiting
let requestQueue = [];
let activeRequests = 0;
const MAX_CONCURRENT_REQUESTS = 8; // Google Translate can handle this well

async function enqueueRequest(fn) {
  return new Promise((resolve, reject) => {
    requestQueue.push({ fn, resolve, reject });
    processQueue();
  });
}

async function processQueue() {
  while (activeRequests < MAX_CONCURRENT_REQUESTS && requestQueue.length > 0) {
    activeRequests++;
    const { fn, resolve, reject } = requestQueue.shift();
    try {
      const result = await fn();
      resolve(result);
    } catch (err) {
      reject(err);
    } finally {
      activeRequests--;
      processQueue();
    }
  }
}

function normalizeCacheKey(text, targetLang) {
  return `${targetLang}::${text.trim().replace(/\s+/g, ' ')}`;
}

function shouldSkipTranslation(text) {
  if (typeof text !== 'string' || !text.trim()) return true;
  if (/<\/[^>]+>/.test(text) || /<[^>]+>/.test(text)) return true;
  if (/^\s*(https?:\/\/|\/|\.|#)/.test(text) && !/\s/.test(text)) return true;
  return false;
}

function extractMathPlaceholders(text) {
  const placeholders = [];
  const patterns = [
    /\$\$[\s\S]*?\$\$/g,
    /\$[^$\n]+\$/g,
    /\\\([\s\S]*?\\\)/g,
    /\\\[[\s\S]*?\\\]/g,
  ];

  let placeholderIndex = 0;
  let protectedText = text;

  for (const pattern of patterns) {
    protectedText = protectedText.replace(pattern, (match) => {
      const token = `__PPX_MATH_${placeholderIndex}__`;
      placeholders.push({ token, value: match });
      placeholderIndex += 1;
      return token;
    });
  }

  return { protectedText, placeholders };
}

function restoreMathPlaceholders(text, placeholders) {
  if (!placeholders || placeholders.length === 0) return text;
  let restored = text;
  placeholders.forEach(({ token, value }) => {
    restored = restored.replace(new RegExp(token, 'g'), value);
  });
  return restored;
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function applyVerifiedHindiGlossary(text) {
  if (typeof text !== 'string' || !text.trim()) return text;

  let normalized = text;

  const exactTerms = Array.from(VERIFIED_EXACT_OVERRIDES.entries()).sort(
    (a, b) => b[0].length - a[0].length
  );

  for (const [source, target] of exactTerms) {
    const pattern = new RegExp(escapeRegExp(source), 'gi');
    normalized = normalized.replace(pattern, target);
  }

  for (const [pattern, replacement] of VERIFIED_RULE_OVERRIDES) {
    normalized = normalized.replace(pattern, replacement);
  }

  for (const [pattern, replacement] of VERIFIED_FIX_REPLACEMENTS) {
    normalized = normalized.replace(pattern, replacement);
  }

  return normalized;
}

async function fetchTranslation(text, targetLang) {
  const normalizedText = text.trim();
  if (!normalizedText) return text;
  const langCode = targetLang === 'hindi' ? 'hi' : targetLang;

  return enqueueRequest(async () => {
    // Only use Google Translate (most stable, reliable)
    // Skip MyMemory to avoid rate limiting
    try {
      const resp = await fetch(
        `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${langCode}&dt=t&q=${encodeURIComponent(normalizedText)}`,
        { signal: AbortSignal.timeout(8000) }
      );
      
      if (resp.ok) {
        const data = await resp.json();
        if (Array.isArray(data) && Array.isArray(data[0])) {
          const translated = data[0].map((seg) => seg[0]).filter(Boolean).join('');
          if (translated.trim()) return translated;
        }
      }
    } catch (err) {
      console.warn('Translation request failed:', err.message);
    }

    return text;
  });
}

export async function translateText(text, targetLang = 'hindi') {
  if (targetLang === 'english' || !text || shouldSkipTranslation(text)) return text;

  const cacheKey = normalizeCacheKey(text, targetLang);
  if (translationCache.has(cacheKey)) {
    return translationCache.get(cacheKey);
  }

  const { protectedText, placeholders } = extractMathPlaceholders(text);
  const translated = await fetchTranslation(protectedText, targetLang);
  const restored = restoreMathPlaceholders(translated, placeholders);
  const finalText = targetLang === 'hindi' ? applyVerifiedHindiGlossary(restored) : restored;

  translationCache.set(cacheKey, finalText);
  return finalText;
}

export async function translateHtmlString(html, targetLang = 'hindi') {
  if (targetLang === 'english') return html;
  if (typeof html !== 'string' || !html.trim()) return html;

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  if (!doc) return html;

  const skipTags = new Set(['script', 'style', 'noscript', 'code', 'pre', 'svg', 'math', 'iframe']);
  const walker = doc.createTreeWalker(doc, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const parentTag = node.parentNode?.nodeName?.toLowerCase();
      if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
      if (skipTags.has(parentTag)) return NodeFilter.FILTER_REJECT;
      if (/^\s*$/.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });

  const nodes = [];
  while (walker.nextNode()) {
    nodes.push(walker.currentNode);
  }

  const maxChunkSize = 1600;
  const chunks = [];
  let currentChunk = [];
  let currentLength = 0;

  for (const node of nodes) {
    const text = node.nodeValue.trim();
    if (!text) continue;
    if (currentLength + text.length > maxChunkSize && currentChunk.length > 0) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentLength = 0;
    }
    currentChunk.push(node);
    currentLength += text.length;
  }
  if (currentChunk.length > 0) chunks.push(currentChunk);

  for (const chunk of chunks) {
    const mapping = chunk.map((node, index) => ({
      token: `__PPX_SEG_${index}__`,
      text: node.nodeValue,
    }));

    const payload = mapping.map((item) => `${item.token}${item.text}`).join('\n');
    const translatedPayload = await translateText(payload, targetLang);

    const parsed = translatedPayload.split(/(__PPX_SEG_\d+__)/g);
    let currentIndex = -1;
    for (const part of parsed) {
      const match = part.match(/^__PPX_SEG_(\d+)__$/);
      if (match) {
        currentIndex = Number(match[1]);
        continue;
      }
      if (currentIndex >= 0 && currentIndex < chunk.length) {
        chunk[currentIndex].nodeValue =
          targetLang === 'hindi' ? applyVerifiedHindiGlossary(part) : part;
      }
    }
  }

  const doctype = '<!doctype html>';
  return doctype + doc.documentElement.outerHTML;
}

const TRANSLATION_SKIP_KEYS = new Set(['svg', 'latex', 'equation', 'file', 'thumb', 'src', 'href', 'path', 'slug', 'id', 'subject']);

export async function translateObjectFields(node, targetLang = 'hindi') {
  if (targetLang === 'english') return node;
  if (node == null) return node;
  if (typeof node === 'string') {
    return shouldSkipTranslation(node) ? node : await translateText(node, targetLang);
  }
  if (Array.isArray(node)) {
    return Promise.all(node.map((item) => translateObjectFields(item, targetLang)));
  }
  if (typeof node === 'object') {
    if ('english' in node || 'hindi' in node) {
      const selected = node[targetLang] ?? node.english ?? node.hindi ?? Object.values(node)[0];
      return translateObjectFields(selected, targetLang);
    }
    const entries = await Promise.all(
      Object.entries(node).map(async ([key, value]) => {
        if (typeof value === 'string' && TRANSLATION_SKIP_KEYS.has(key)) {
          return [key, value];
        }
        return [key, await translateObjectFields(value, targetLang)];
      })
    );
    return Object.fromEntries(entries);
  }
  return node;
}
